import type { Incoterms } from 'api';
import { PortAutocomplete } from 'form';
import { useTranslation } from 'i18n';
import { useContext } from 'react';
import { Box, Tooltip } from 'ui';
import { InfoOutlined } from 'ui/icons';

import { PurchaseOrderFormContext } from '../../contexts/PurchaseOrderFormContext';

const INCOTERM_CONFIG = {
  CFR: { portOfLoadingRequired: false, showICD: true, showPortOfDischarge: true, showPortOfLoading: true },
  CIF: { portOfLoadingRequired: false, showICD: true, showPortOfDischarge: true, showPortOfLoading: true },
  EXW: { portOfLoadingRequired: false, showICD: false, showPortOfDischarge: false, showPortOfLoading: true },
  FAS: { portOfLoadingRequired: true, showICD: false, showPortOfDischarge: false, showPortOfLoading: true },
  FCA: { portOfLoadingRequired: true, showICD: false, showPortOfDischarge: false, showPortOfLoading: true },
  FOB: { portOfLoadingRequired: true, showICD: false, showPortOfDischarge: false, showPortOfLoading: true },
} as const;

type IncotermConfigKey = keyof typeof INCOTERM_CONFIG;

interface Props {
  incoterm?: Incoterms;
}

export const IncotermSpecificFields = ({ incoterm }: Props) => {
  const { t } = useTranslation('orders');
  const { defaultValues } = useContext(PurchaseOrderFormContext);
  if (!incoterm || !(incoterm in INCOTERM_CONFIG)) {
    return null;
  }

  const config = INCOTERM_CONFIG[incoterm as IncotermConfigKey];

  return (
    <>
      {config.showPortOfLoading ? (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 1,
          }}
        >
          <PortAutocomplete
            name="port_of_loading_id"
            label={t('buyerLogisticsSection.labels.portOfLoading')}
            required={config.portOfLoadingRequired}
            defaultValue={defaultValues?.port_of_loading_id}
          />
          <Tooltip title={t('buyerLogisticsSection.tooltips.portOfLoading')}>
            <InfoOutlined />
          </Tooltip>
        </Box>
      ) : null}
      {config.showPortOfDischarge ? (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 1,
          }}
        >
          <PortAutocomplete
            name="port_of_discharge_id"
            label={t('buyerLogisticsSection.labels.portOfDischarge')}
            required
            defaultValue={defaultValues?.port_of_discharge_id}
          />
          <Tooltip title={t('buyerLogisticsSection.tooltips.portOfDischarge')}>
            <InfoOutlined />
          </Tooltip>
        </Box>
      ) : null}
      {config.showICD ? (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 1,
          }}
        >
          <PortAutocomplete
            type="ICD"
            name="icd_id"
            label={t('incotermSpecificFields.labels.inlandContainerDepot')}
            required={false}
            defaultValue={defaultValues?.icd_id}
          />
          <Tooltip title={t('incotermSpecificFields.tooltips.inlandContainerDepot')}>
            <InfoOutlined />
          </Tooltip>
        </Box>
      ) : null}
    </>
  );
};
